import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Fade,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Stop as StopIcon,
  Mic as MicIcon,
  VolumeUp as VolumeIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { useRecording } from '../contexts/SimpleRecordingContext';

const LiveTranscriptionWindow = () => {
  const {
    isRecording,
    currentTranscript,
    recordingTime,
    enableRealTimeTranscription,
    setEnableRealTimeTranscription,
    connectionStatus,
    toggleRecording,
    projectId
  } = useRecording();

  const [isMinimized, setIsMinimized] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [isUserScrolling, setIsUserScrolling] = useState(false);

  const transcriptRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());

  // ENTERPRISE: Debounced text updates for performance
  const updateDisplayText = useCallback((newText) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    // Buffer updates if they're coming too frequently (< 300ms)
    if (timeSinceLastUpdate < 300 && newText !== currentTranscript) {
      return;
    }

    lastUpdateRef.current = now;
    setDisplayText(newText);
  }, [currentTranscript]);

  // PERFORMANCE: Throttled transcript updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateDisplayText(currentTranscript);
    }, 200); // 200ms debounce for smooth UX

    return () => clearTimeout(timeoutId);
  }, [currentTranscript, updateDisplayText]);

  // ENTERPRISE: Intelligent auto-scroll with reading detection
  useEffect(() => {
    if (!transcriptRef.current || !displayText) return;

    const element = transcriptRef.current;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 30;

    // Only auto-scroll if user is not actively reading AND they are near the bottom
    if (!isUserScrolling && isNearBottom) {
      // Use instant scrolling to avoid visual distraction during typing
      element.scrollTop = element.scrollHeight;
    }
  }, [displayText, isUserScrolling]);

  // DETECT: User scroll behavior
  const handleScroll = useCallback(() => {
    setIsUserScrolling(true);

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Reset scroll state after 2 seconds of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 2000);
  }, []);

  // Format recording time
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  // Don't show if not recording OR if using dialog-based recording (AddVoiceDialog)
  // Check if recording was started from a project context (has projectId)
  if (!isRecording || projectId) return null;

  return (
    <Fade in={isRecording}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          // FIXED: Stable dimensions - no dynamic sizing
          width: 450,
          height: isMinimized ? 80 : 400,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          // SUBTLE: Enterprise-grade shadow
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1)',
          // PERFORMANCE: GPU acceleration
          transform: 'translateZ(0)',
          willChange: 'height',
          transition: 'height 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Header - STABLE & MINIMAL */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'grey.100',
          borderBottom: '1px solid',
          borderColor: 'divider',
          // STABLE: Fixed height to prevent layout shift
          height: 56,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* SUBTLE: Static recording indicator */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'error.main',
                // REMOVED: No pulsing animation
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Recording
              </Typography>
            </Box>

            {/* CLEAN: Time display */}
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
              minWidth: '35px',
              fontSize: '0.8rem'
            }}>
              {formatTime(recordingTime)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setIsMinimized(!isMinimized)}
              sx={{
                color: 'text.secondary',
                p: 0.5,
                '&:hover': { color: 'text.primary' }
              }}
              title={isMinimized ? "Show" : "Minimize"}
            >
              {isMinimized ? <ShowIcon fontSize="small" /> : <HideIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={toggleRecording}
              sx={{
                color: 'error.main',
                p: 0.5,
                '&:hover': { bgcolor: 'error.main', color: 'white' }
              }}
              title="Stop Recording"
            >
              <StopIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={toggleRecording}
              sx={{
                color: 'text.secondary',
                p: 0.5,
                '&:hover': { color: 'text.primary' }
              }}
              title="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {!isMinimized && (
          <>
            {/* Controls - MINIMAL & CALM */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableRealTimeTranscription}
                      onChange={(e) => setEnableRealTimeTranscription(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Live transcription"
                  sx={{
                    fontSize: '0.8rem',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.8rem'
                    }
                  }}
                />

                <Chip
                  label={connectionStatus}
                  size="small"
                  color={
                    connectionStatus === 'connected' ? 'success' :
                    connectionStatus === 'processing' ? 'warning' :
                    connectionStatus === 'error' ? 'error' : 'default'
                  }
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              </Box>
            </Box>

            {/* Transcript Display - STABLE & CALM */}
            <Box
              ref={transcriptRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                p: 2,
                overflow: 'auto',
                bgcolor: 'background.default',
                // FIXED: Stable height - no dynamic sizing
                height: 300,
                '&::-webkit-scrollbar': {
                  width: 4,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: 2,
                },
              }}
            >
              {displayText ? (
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.9rem',
                    color: 'text.primary',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    // STABLE: No transitions or animations
                  }}
                >
                  {displayText}
                </Typography>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <VolumeIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                  <Typography variant="body2" align="center" sx={{ fontSize: '0.85rem' }}>
                    {enableRealTimeTranscription
                      ? 'Listening for speech...'
                      : 'Enable live transcription to see text here'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer - MINIMAL INFO */}
            <Box sx={{
              px: 2,
              py: 1,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ fontSize: '0.7rem' }}>
                Transcript saves automatically when recording stops
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Fade>
  );
};

export default LiveTranscriptionWindow;